import { Injectable } from "@angular/core";
import { catchError, map, Observable, of, ReplaySubject, tap } from "rxjs";
import { environment } from "@env/environment";
import { HttpClient } from "@angular/common/http";
import { Profile, Permission } from "./profile.interface";


@Injectable({
    providedIn: 'root'
})
export class ProfileService {

    baseUrl = environment.auth_url + '/admin/profiles';
    private _profile: ReplaySubject<Profile> = new ReplaySubject<Profile>(1);
    private _profiles: ReplaySubject<Profile[]> = new ReplaySubject<Profile[]>(1);

    private _permissions: ReplaySubject<Permission[]> = new ReplaySubject<Permission[]>(1);

    private _metadata: ReplaySubject<any> = new ReplaySubject<any>(1);

    set profile(value: Profile) {
        this._profile.next(value);
    }

    get profile$() {
        return this._profile.asObservable();
    }

    set profiles(value: Profile[]) {
        this._profiles.next(value);
    }

    get profiles$() {
        return this._profiles.asObservable();
    }

    set permissions(value: Permission[]) {
        this._permissions.next(value);
    }

    get permissions$() {
        return this._permissions.asObservable();
    }

    set metadata(value: any) {
        this._metadata.next(value);
    }

    get metadata$() {
        return this._metadata.asObservable();
    }

    constructor(
        private _httpClient: HttpClient
    ) { }


    create(profile: any): Observable<Profile> {
        const profileData = profile instanceof Profile ? profile.toJSON() : {
            name: profile.name,
            description: profile.description,
            level: profile.level,
            permissions: profile.permissions,
            restrictions: profile.restrictions || null,
            dataVisibility: profile.dataVisibility || 'OWN_ENTITY'
        };

        return this._httpClient.post<any>(`${this.baseUrl}`, profileData)
            .pipe(
                tap((response) => {
                    const deserializedProfile = Profile.fromJSON(response);
                    this.profile = deserializedProfile;
                    return deserializedProfile;
                }),
                catchError((error) => {
                    throw error;
                })
            );
    }

    get(): Observable<Profile> {
        return this._httpClient.get<any>(`${this.baseUrl}/me`)
            .pipe(
                tap((response) => {
                    const deserializedProfile = Profile.fromJSON(response);
                    this.profile = deserializedProfile;
                    return deserializedProfile;
                }),
                catchError((error) => {
                    return of(error);
                })
            );
    }

    getAll(): Observable<Profile[]> {
        return this._httpClient.get<any[]>(`${this.baseUrl}`)
            .pipe(
                tap((response: any[]) => {
                    const deserializedProfiles = response.map((profileData: any) =>
                        Profile.fromJSON(profileData)
                    );
                    this.profiles = deserializedProfiles;
                }),
                catchError(() => of([] as Profile[]))
            );
    }

    getAllPermissions(): Observable<Permission[]>  {
        return this._httpClient.get<any[]>(`${this.baseUrl}/permissions`)
            .pipe(
                map((permissions) => (permissions || []).map((p) => ({
                    uuid: p.uuid ?? p.id,
                    name: p.name,
                    domain: p.domain,
                    readOnly: p.readOnly,
                    crownJewel: p.crownJewel,
                } as Permission))),
                tap((permissions) => {
                    this.permissions = permissions;
                }),
                catchError(() => of([] as Permission[]))
            );
    }

    delete(id: string): Observable<void> {
        return this._httpClient.delete<void>(`${this.baseUrl}/${id}`)
            .pipe(
                tap(() => { }),
                catchError((error) => {
                    return of(error.error);
                })
            );
    }

    update(profile: any): Observable<Profile> {
        const profileData = profile instanceof Profile ? profile.toJSON() : profile;

        return this._httpClient.put<any>(`${this.baseUrl}/${profile.id}`, profileData)
            .pipe(
                tap((response) => {
                    const deserializedProfile = Profile.fromJSON(response);
                    this.profile = deserializedProfile;
                    return deserializedProfile;
                }),
                catchError((error) => {
                    return of(error);
                })
            );
    }
}
